import numeral from "numeral"
import { formatTime } from "../../../utils/date"
import { canUseNumeral } from "../../../utils/numerals"
import { numberFormatter as sharedNumberFormatter } from "../../../utils/sharedFormatter"
import { SERIE_FORMAT } from "./ChartOptions"
import {
  type FormatterOptions,
  bytesFormatter as sharedBytesFormatter,
} from "./sharedFormatter"
import type { Highcharts } from "./useHighcharts"

type ValueFormatter<T = Highcharts.Point> = (
  formatterOptions: FormatterOptions
) => (this: T) => string

const pricingFormatter: ValueFormatter = function pricingFormatter() {
  return (value = 0) => {
    // Tiny values keep 3 significant digits ($0.0₅662), like legends and tables
    if (value !== 0 && Math.abs(value) < 0.01) {
      return sharedNumberFormatter({
        value,
        abbreviationFormat: "full",
        prefix: "$",
      })
    }

    let finalValue

    if (canUseNumeral(value)) {
      finalValue =
        Math.abs(value) < 1000
          ? numeral(value).format("$0,0.00[000]a")
          : numeral(value).format("$0.000a")
    } else {
      finalValue = `${value}`
    }

    return finalValue
  }
}

const ratioFormatter: ValueFormatter = function ratioFormatter() {
  return (value = 0) => `${numeral(value).format("0,0.00")}x`
}

const numberFormatter: ValueFormatter = function numberFormatter(options) {
  return (value = 0) => {
    const rounded = Number.isNaN(parseFloat(numeral(value).format("0.0a")))
      ? 0
      : numeral(value).format("0.0a")

    return [
      // More precise formatting for small values
      value < 1000
        ? numeral(value).format(Math.abs(value) > 100 ? "0,0" : "0[.]00a")
        : "",
      value > 1000
        ? `${rounded}${options.postfix ? " " : ""}`
        : `${options.postfix ?? ""}`,
    ].join(" ")
  }
}

const tokenFormatter: ValueFormatter = function tokenFormatter(options) {
  return (value = 0) => {
    const rounded = Number.isNaN(parseFloat(numeral(value).format("0.0a")))
      ? 0
      : numeral(value).format("0.0a")

    return `${rounded} ${options.token ?? ""}`
  }
}

const percentageFormatter: ValueFormatter = function percentageFormatter(
  options
) {
  return function (value = 0) {
    /*
     * Value of percent data is either percentage which comes from Show as % share (inbuilt via stack)
     * or from the point value (in case serie is percentage value serie)
     */
    let finalValue: number
    // If is bar chart, abide to percentage value if known
    /*
     * Lets try out this logic. The aim here is to allow BarCharts to use "% share" via this.percentage
     * while supporting SERIE_FORMAT.PERCENTAGE too.
     * For non BarChart we only support serie value, because % share probably never makes sense in those cases.
     *
     * TODO: Make this less of a hack. We could dig into context, but probably not in UI level, so that means prop, which means re-rendering possibly. Maybe should be an arg.
     */
    if (options.isPercentageShare) {
      finalValue = this?.percentage ?? value
    } else {
      finalValue = value * 100
    }

    return `${numeral(finalValue).format("0,0.00")}%`
  }
}

const timeFormatter: ValueFormatter = function timeFormatter() {
  return (value = 0) => (typeof value === "number" ? formatTime(value) : "-")
}

const bytesFormatter: ValueFormatter = function timeFormatter() {
  return (value = 0) =>
    typeof value === "number" ? sharedBytesFormatter(value) : "-"
}

const formatters = {
  [SERIE_FORMAT.PRICE]: pricingFormatter,
  [SERIE_FORMAT.RATIO]: ratioFormatter,
  [SERIE_FORMAT.PERCENTAGE]: percentageFormatter,
  [SERIE_FORMAT.NUMBER]: numberFormatter,
  [SERIE_FORMAT.TOKEN]: tokenFormatter,
  [SERIE_FORMAT.TIME]: timeFormatter,
  [SERIE_FORMAT.BYTES]: bytesFormatter,
  [SERIE_FORMAT.TOKEN_ICP]: (options: FormatterOptions) =>
    tokenFormatter({
      ...options,
      token: "ICP",
    }),
  [SERIE_FORMAT.TOKEN_CKBTC]: (options: FormatterOptions) =>
    tokenFormatter({
      ...options,
      token: "ckBTC",
    }),
  [SERIE_FORMAT.TOKEN_CKETH]: (options: FormatterOptions) =>
    tokenFormatter({
      ...options,
      token: "ckETH",
    }),
  [SERIE_FORMAT.CYCLES_PER_SECONDS]: (options: FormatterOptions) =>
    numberFormatter({
      ...options,
      postfix: "cycles/second",
    }),
  [SERIE_FORMAT.KWH]: (options: FormatterOptions) =>
    numberFormatter({
      ...options,
      postfix: "kWh",
    }),
} as const

/** @deprecated */
export function getValueFormatter(
  serie: `${SERIE_FORMAT}`,
  options: FormatterOptions = {}
): (value: number) => string {
  return formatters[serie](options)
}
